import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import Button from '../components/ui/Button';
import { Input, Select, Label } from '../components/ui/Input';
import DataGrid from '../components/grid/DataGrid';
import BulkEditBar from '../components/grid/BulkEditBar';
import BulkPreviewModal from '../components/grid/BulkPreviewModal';

// Unified Scan Item Master (migration 026). The Majans-curated classification
// that used to live in ITEMMASTER.xlsx TOTALGROCERY — now governed SQL.
// AG Grid (Community) over the server-paged /scan-items API via the Infinite
// Row Model: filter controls drive the datasource, double-click edits a cell
// (PUT /scan-items/{ean}), and select-rows → Set field routes the bulk edit
// through the existing dry-run + audit importer. The review queue (status=draft)
// is the new-item gap-closing workflow.

const EDIT_FIELDS = [
  ['productDescription', 'Product Description'], ['subCategory', 'Sub-Category'],
  ['segment', 'Segment'], ['brand', 'Brand'], ['manufacturer', 'Manufacturer'],
  ['sizeGrouping', 'Size Grouping'], ['sizeG', 'Size (g)'], ['flavourCategory', 'Flavour Category'],
  ['flavours', 'Flavours'], ['base', 'Base'], ['assortment', 'Assortment'],
  ['priceTier', 'Price Tier'], ['majansItemCode', 'Majans Item Code'],
];
const EXPORT_KEYS = ['ean', 'majansItemCode', 'productDescription', 'subCategory', 'segment',
  'brand', 'manufacturer', 'sizeGrouping', 'sizeG', 'flavourCategory', 'flavours', 'base',
  'assortment', 'priceTier', 'isActive', 'removed', 'ownBrand', 'curationStatus'];

// Fields offered in the bulk "Set X = Y" picker (the safe-to-batch text fields).
const BULK_FIELDS = [
  ['subCategory', 'Sub-Category'], ['segment', 'Segment'], ['brand', 'Brand'],
  ['manufacturer', 'Manufacturer'], ['sizeGrouping', 'Size Grouping'],
  ['flavourCategory', 'Flavour Category'], ['base', 'Base'], ['assortment', 'Assortment'],
  ['priceTier', 'Price Tier'],
].map(([key, label]) => ({ key, label }));
const LABEL_BY_KEY = Object.fromEntries([...EDIT_FIELDS, ...BULK_FIELDS.map((f) => [f.key, f.label])]);

const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};
const CHIP = { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, border: '1px solid var(--border-soft)', color: 'var(--text-muted)' };

export default function ScanItems() {
  const apiFetch = useApi();
  const qc = useQueryClient();
  const fileRef = useRef(null);

  const [status, setStatus] = useState('All');
  const [ownBrand, setOwnBrand] = useState('All');
  const [q, setQ] = useState('');
  const [editor, setEditor] = useState(null);
  const [importState, setImportState] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [gridApi, setGridApi] = useState(null);
  const [displayedCount, setDisplayedCount] = useState(0);
  const [selectedCount, setSelectedCount] = useState(0);
  const [gridError, setGridError] = useState(null);
  const [bulk, setBulk] = useState(null); // { field, value, rows, lines?, applying, done?, error? }

  const { data: stats } = useQuery({
    queryKey: ['scan-item-stats'],
    queryFn: () => apiFetch('/scan-items/stats').then((r) => r.json()),
    staleTime: 60_000,
  });

  // Page-level filter → server query params (drives the ?all=true fetch).
  const filterParams = useCallback(() => {
    const p = new URLSearchParams();
    if (status !== 'All') p.set('status', status.toLowerCase());
    if (ownBrand !== 'All') p.set('ownBrand', ownBrand === 'Own brand' ? 'true' : 'false');
    if (q.trim()) p.set('q', q.trim());
    return p;
  }, [status, ownBrand, q]);

  // Client-side row model: load the whole filtered set in one call (?all=true)
  // so AG Grid's column filters (blank, contains, equals, …) work natively. The
  // page-level Status/Own brand/Search controls narrow the server fetch; the
  // grid's column filters then refine client-side on whatever's loaded.
  const { data, isLoading, error } = useQuery({
    queryKey: ['scan-items', status, ownBrand, q],
    queryFn: () => {
      const sp = filterParams();
      sp.set('all', 'true');
      return apiFetch(`/scan-items?${sp.toString()}`).then((r) => { if (!r.ok) throw new Error(`API ${r.status}`); return r.json(); });
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const rows = data?.rows ?? [];

  const onGridReady = useCallback((e) => setGridApi(e.api), []);
  const refreshGrid = useCallback(() => qc.invalidateQueries({ queryKey: ['scan-items'] }), [qc]);
  // Footer/export count = rows visible after the AG Grid column filters.
  const updateDisplayed = useCallback((e) => setDisplayedCount(e.api.getDisplayedRowCount()), []);

  // Drop any selection when the page-level filter changes the loaded set.
  // deselectAll() fires onSelectionChanged, which resets selectedCount for us.
  useEffect(() => { gridApi?.deselectAll(); }, [status, ownBrand, q, gridApi]);

  // Inline single-cell edit → partial PUT. Pass curationStatus so editing a
  // draft cell doesn't silently curate it. Revert the cell on failure.
  const onCellValueChanged = useCallback(async (e) => {
    const field = e.colDef.field;
    if (!field || e.newValue === e.oldValue) return;
    const body = { [field]: e.newValue, curationStatus: e.data.curationStatus };
    try {
      const res = await apiFetch(`/scan-items/${encodeURIComponent(e.data.ean)}`, { method: 'PUT', body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? `Save failed (${res.status})`);
      setGridError(null);
      qc.invalidateQueries({ queryKey: ['scan-item-stats'] });
    } catch (err) {
      setGridError(`${e.data.ean}: ${String(err.message ?? err)}`);
      e.node.setDataValue(field, e.oldValue);
    }
  }, [apiFetch, qc]);

  const columns = useMemo(() => [
    {
      headerName: '', field: '__open', pinned: 'left', width: 64, sortable: false, filter: false,
      editable: false, resizable: false, suppressMovable: true,
      cellRenderer: (p) => (p.data
        ? <button onClick={() => setEditor({ ean: p.data.ean, row: p.data })}
            style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }}>Open</button>
        : null),
    },
    { headerName: 'EAN', field: 'ean', width: 130, editable: false },
    { headerName: 'Product', field: 'productDescription', flex: 1, minWidth: 220, editable: true },
    { headerName: 'Brand', field: 'brand', width: 120, editable: true },
    { headerName: 'Sub-Category', field: 'subCategory', width: 150, editable: true },
    { headerName: 'Segment', field: 'segment', width: 120, editable: true },
    { headerName: 'Manufacturer', field: 'manufacturer', width: 150, editable: true },
    { headerName: 'Size (g)', field: 'sizeG', width: 95, editable: true, cellDataType: 'number' },
    { headerName: 'Own', field: 'ownBrand', width: 80, editable: true, cellDataType: 'boolean' },
    {
      headerName: 'Status', field: 'curationStatus', width: 100, editable: false,
      valueFormatter: (p) => (p.value === 'draft' ? 'Review' : 'Curated'),
    },
    { headerName: 'Updated', field: 'updatedAt', width: 130, editable: false, filter: false, valueFormatter: (p) => fmtDate(p.value) },
  ], []);

  const getRowStyle = useCallback(
    (p) => (p.data?.curationStatus === 'draft' ? { background: 'var(--warn-soft)' } : undefined), []);

  async function exportExcel() {
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      // Export exactly what's visible — respects the column filters + sort.
      const out = [];
      if (gridApi) gridApi.forEachNodeAfterFilterAndSort((n) => { if (n.data) out.push(n.data); });
      else out.push(...rows);
      const sheet = out.map((s) => Object.fromEntries(EXPORT_KEYS.map((k) =>
        [k, typeof s[k] === 'boolean' ? (s[k] ? 1 : 0) : s[k] ?? ''])));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet), 'ScanItems');
      XLSX.writeFile(wb, `ScanItems_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } finally {
      setExporting(false);
    }
  }

  async function onImportFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const XLSX = await import('xlsx');
    const wb = XLSX.read(await file.arrayBuffer());
    const raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
    const norm = (h) => String(h).toLowerCase().replace(/[^a-z0-9]/g, '');
    const KEYMAP = {};
    for (const k of EXPORT_KEYS) KEYMAP[norm(k)] = k;
    Object.assign(KEYMAP, { ean: 'ean', productdescription: 'productDescription', subcategory: 'subCategory',
      sizeg: 'sizeG', sizegrouping: 'sizeGrouping', flavourcategory: 'flavourCategory',
      majansitemcode: 'majansItemCode', isactive: 'isActive', remove: 'removed' });
    const rows2 = raw.map((r) => {
      const o = {};
      for (const [h, v] of Object.entries(r)) { const k = KEYMAP[norm(h)]; if (k && v !== '') o[k] = v; }
      return o;
    }).filter((r) => r.ean);
    if (!rows2.length) { setImportState({ fileName: file.name, error: 'No rows with an EAN column found.' }); return; }
    setImportState({ fileName: file.name, rows: rows2, committing: false });
    try {
      const res = await apiFetch('/scan-items/import', { method: 'POST', body: JSON.stringify({ rows: rows2, dryRun: true, fileName: file.name }) });
      const preview = await res.json();
      if (!res.ok) throw new Error(preview?.error ?? `Preview failed (${res.status})`);
      setImportState((s) => ({ ...s, preview }));
    } catch (err) { setImportState((s) => ({ ...s, error: String(err.message ?? err) })); }
  }

  async function commitImport() {
    setImportState((s) => ({ ...s, committing: true }));
    try {
      const { rows: r2, fileName } = importState;
      const res = await apiFetch('/scan-items/import', { method: 'POST', body: JSON.stringify({ rows: r2, dryRun: false, fileName }) });
      const result = await res.json();
      if (!res.ok) throw new Error(result?.error ?? `Import failed (${res.status})`);
      qc.invalidateQueries({ queryKey: ['scan-item-stats'] });
      refreshGrid();
      setImportState((s) => ({ ...s, committing: false, result }));
    } catch (err) { setImportState((s) => ({ ...s, committing: false, error: String(err.message ?? err) })); }
  }

  // ── Governed bulk edit (select rows → Set field = value → dry-run → apply) ──
  async function onBulkApply(field, value) {
    const rows = gridApi?.getSelectedRows() ?? [];
    if (!rows.length) return;
    const importRows = rows.map((r) => ({ ean: r.ean, [field]: value, curationStatus: r.curationStatus }));
    setBulk({ field, value, rows: importRows, applying: false });
    try {
      const res = await apiFetch('/scan-items/import', { method: 'POST', body: JSON.stringify({ rows: importRows, dryRun: true, fileName: `bulk:${field}` }) });
      const preview = await res.json();
      if (!res.ok) throw new Error(preview?.error ?? `Preview failed (${res.status})`);
      setBulk((s) => ({ ...s, lines: [
        `${preview.updates.toLocaleString()} item(s) will be updated.`,
        `Set ${LABEL_BY_KEY[field] ?? field} = ${value === '' ? '(blank)' : value}.`,
        ...(preview.errors.length ? [`${preview.errors.length} row(s) skipped (invalid EAN).`] : []),
      ] }));
    } catch (err) { setBulk((s) => ({ ...s, error: String(err.message ?? err) })); }
  }

  async function commitBulk() {
    setBulk((s) => ({ ...s, applying: true }));
    try {
      const res = await apiFetch('/scan-items/import', { method: 'POST', body: JSON.stringify({ rows: bulk.rows, dryRun: false, fileName: `bulk:${bulk.field}` }) });
      const result = await res.json();
      if (!res.ok) throw new Error(result?.error ?? `Apply failed (${res.status})`);
      qc.invalidateQueries({ queryKey: ['scan-item-stats'] });
      gridApi?.deselectAll();
      setSelectedCount(0);
      refreshGrid();
      setBulk((s) => ({ ...s, applying: false, done: `Done — ${result.updates} updated, ${result.errors.length} errors.` }));
    } catch (err) { setBulk((s) => ({ ...s, applying: false, error: String(err.message ?? err) })); }
  }

  const setFilter = (fn) => (v) => { fn(v); };

  const saveMutation = useMutation({
    mutationFn: async ({ ean, body }) => {
      const res = await apiFetch(`/scan-items/${encodeURIComponent(ean)}`, { method: 'PUT', body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error ?? `Save failed (${res.status})`);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scan-item-stats'] });
      refreshGrid();
      setEditor(null);
    },
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {stats && <>
          <span style={CHIP}>{stats.total?.toLocaleString()} items</span>
          <span style={{ ...CHIP, color: 'var(--warn-text)', borderColor: 'var(--warn-border)', cursor: 'pointer' }}
                onClick={() => setStatus('Draft')}>{stats.draft?.toLocaleString()} to review</span>
          <span style={CHIP}>{stats.curated?.toLocaleString()} curated</span>
          <span style={CHIP}>{stats.ownBrand?.toLocaleString()} own-brand</span>
        </>}
        <span style={{ flex: 1 }} />
        <Button size="sm" onClick={() => fileRef.current?.click()}>Import Excel</Button>
        <Button size="sm" variant="primary" disabled={exporting} onClick={exportExcel}>
          {exporting ? 'Exporting…' : `Export (${displayedCount.toLocaleString()})`}
        </Button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={onImportFile} />
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ width: 150 }}>
          <Label>Status</Label>
          <Select value={status} onChange={(e) => setFilter(setStatus)(e.target.value)}>
            {['All', 'Draft', 'Curated'].map((s) => <option key={s}>{s}</option>)}
          </Select>
        </div>
        <div style={{ width: 150 }}>
          <Label>Own brand</Label>
          <Select value={ownBrand} onChange={(e) => setFilter(setOwnBrand)(e.target.value)}>
            {['All', 'Own brand', 'Competitor'].map((s) => <option key={s}>{s}</option>)}
          </Select>
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <Label>Search</Label>
          <Input placeholder="Product, EAN, brand, Majans code…" value={q} onChange={(e) => setFilter(setQ)(e.target.value)} />
        </div>
      </div>

      <BulkEditBar
        selectedCount={selectedCount}
        fields={BULK_FIELDS}
        noun="items"
        applying={!!bulk && !bulk.done && !bulk.error}
        onApply={onBulkApply}
        onClear={() => { gridApi?.deselectAll(); setSelectedCount(0); }}
      />

      {error && <div style={{ fontSize: 12, color: 'var(--danger)' }}>Failed to load scan items: {String(error.message ?? error)}</div>}
      {gridError && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{gridError}</div>}

      <DataGrid
        rowData={rows}
        columnDefs={columns}
        onGridReady={onGridReady}
        onCellValueChanged={onCellValueChanged}
        onSelectionChanged={(e) => setSelectedCount(e.api.getSelectedRows().length)}
        onModelUpdated={updateDisplayed}
        rowSelection={{ mode: 'multiRow', checkboxes: true, headerCheckbox: true, selectAll: 'filtered', enableClickSelection: false }}
        getRowId={(p) => String(p.data.ean)}
        getRowStyle={getRowStyle}
        loading={isLoading}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: 'var(--text-muted)' }}>
        <span>{displayedCount.toLocaleString()} items{selectedCount ? ` · ${selectedCount.toLocaleString()} selected` : ''}</span>
        <span style={{ flex: 1 }} />
        <span>Filter any column header · double-click a cell to edit · tick rows to bulk-edit · Open for the full record</span>
      </div>

      {editor && <EditDrawer ean={editor.ean} initial={editor.row} onClose={() => setEditor(null)}
        onSave={(ean, body) => saveMutation.mutate({ ean, body })} saving={saveMutation.isPending} saveError={saveMutation.error} />}
      {importState && <ImportModal state={importState} onCommit={commitImport} onClose={() => setImportState(null)} />}
      {bulk && <BulkPreviewModal title="Bulk update scan items" lines={bulk.lines} error={bulk.error}
        applying={bulk.applying} done={bulk.done} onApply={commitBulk} onClose={() => setBulk(null)} />}
    </div>
  );
}

function EditDrawer({ ean, initial, onClose, onSave, saving, saveError }) {
  const apiFetch = useApi();
  const { data: full } = useQuery({
    queryKey: ['scan-item', ean],
    queryFn: () => apiFetch(`/scan-items/${encodeURIComponent(ean)}`).then((r) => r.json()),
    staleTime: 30_000,
  });
  const [form, setForm] = useState(() => ({ ...initial }));
  const retailers = full?.retailers ?? [];
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function submit() {
    const body = {};
    for (const [k] of EDIT_FIELDS) if (form[k] !== undefined) body[k] = form[k] === '' ? null : form[k];
    body.isActive = form.isActive === undefined ? true : !!form.isActive;
    body.removed = !!form.removed;
    body.ownBrand = !!form.ownBrand;
    onSave(ean, body); // curating → API sets status=curated
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} onClick={onClose} />
      <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(560px, 96vw)', background: 'var(--bg-card)', borderLeft: '1px solid var(--border)', boxShadow: '-8px 0 24px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{form.productDescription ?? ean}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>EAN {ean} · {form.curationStatus === 'draft' ? 'Needs review' : 'Curated'}{form.ownBrand ? ' · Own brand' : ''}</div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>✕</Button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {form.curationStatus === 'draft' && (
            <div style={{ fontSize: 12, color: 'var(--warn-text)', background: 'var(--warn-soft)', border: '1px solid var(--warn-border)', borderRadius: 'var(--radius)', padding: '8px 10px' }}>
              New item from scan data — confirm/adjust the Majans classification below to curate it.
            </div>
          )}
          <section>
            <Label style={{ marginBottom: 8 }}>Majans classification</Label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {EDIT_FIELDS.map(([k, label]) => (
                <div key={k} style={{ gridColumn: k === 'productDescription' ? '1 / -1' : undefined }}>
                  <Label style={{ textTransform: 'none', letterSpacing: 0 }}>{label}</Label>
                  <Input value={form[k] ?? ''} onChange={set(k)} />
                </div>
              ))}
              <div>
                <Label style={{ textTransform: 'none', letterSpacing: 0 }}>Active</Label>
                <Select value={form.isActive ? 'Yes' : 'No'} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.value === 'Yes' }))}><option>Yes</option><option>No</option></Select>
              </div>
              <div>
                <Label style={{ textTransform: 'none', letterSpacing: 0 }}>Own brand</Label>
                <Select value={form.ownBrand ? 'Yes' : 'No'} onChange={(e) => setForm((f) => ({ ...f, ownBrand: e.target.value === 'Yes' }))}><option>No</option><option>Yes</option></Select>
              </div>
            </div>
          </section>

          <section>
            <Label style={{ marginBottom: 8 }}>Retailer classification (auto-derived from scan)</Label>
            {retailers.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No retailer scan rows for this EAN.</div>}
            {retailers.map((r) => (
              <div key={r.customerCode} style={{ fontSize: 12, color: 'var(--text-body)', borderBottom: '1px solid var(--border-soft)', padding: '6px 0' }}>
                <div style={{ fontWeight: 600 }}>{r.customerName ?? r.customerCode}</div>
                <div style={{ color: 'var(--text-muted)' }}>
                  {[r.subCategory, r.segment, r.brand, r.supplier].filter(Boolean).join(' · ') || '—'}
                  {r.productCode ? ` · #${r.productCode}` : ''}
                </div>
              </div>
            ))}
          </section>
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center' }}>
          {saveError && <span style={{ fontSize: 12, color: 'var(--danger)', flex: 1 }}>{String(saveError.message ?? saveError)}</span>}
          {!saveError && <span style={{ flex: 1 }} />}
          <Button size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant="primary" disabled={saving} onClick={submit}>
            {saving ? 'Saving…' : (form.curationStatus === 'draft' ? 'Curate' : 'Save')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ImportModal({ state, onCommit, onClose }) {
  const { fileName, preview, committing, result, error } = state;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 'min(560px, 94vw)', maxHeight: '84vh', overflow: 'auto', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Scan item import — {fileName}</div>
        {error && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>}
        {!error && !preview && !result && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Analysing file…</div>}
        {!error && preview && !result && (<>
          <div style={{ fontSize: 13, color: 'var(--text-body)' }}>
            {preview.total.toLocaleString()} rows · <b>{preview.adds}</b> new · <b>{preview.updates}</b> updates · {preview.errors.length} errors
          </div>
          {preview.errors.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 120, overflow: 'auto', fontSize: 12, color: 'var(--text-body)' }}>
              {preview.errors.slice(0, 25).map((e2, i) => <li key={i}>Row {e2.row}: {e2.error}</li>)}
            </ul>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" variant="primary" disabled={committing || (preview.adds === 0 && preview.updates === 0)} onClick={onCommit}>
              {committing ? 'Importing…' : 'Apply import'}
            </Button>
          </div>
        </>)}
        {result && (<>
          <div style={{ fontSize: 13, color: 'var(--text-body)' }}>Done — {result.adds} new, {result.updates} updated, {result.errors.length} errors.</div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}><Button size="sm" variant="primary" onClick={onClose}>Close</Button></div>
        </>)}
      </div>
    </div>
  );
}
